import { Injectable } from '@angular/core';
import { Observable, from } from 'rxjs';
import * as CryptoJS from 'crypto-js';

/**
 * Must match server `ENCRYPTION_KEY`.
 */
const ENCRYPTION_KEY_HEX = '826e985fd038d5ea9fe2e8b72fdc7f1d';

function hexToBytes(hex: string): Uint8Array {
  const clean = hex.replace(/\s/g, '');
  if (clean.length % 2 !== 0) {
    throw new Error('Invalid hex length');
  }
  const out = new Uint8Array(clean.length / 2);
  for (let i = 0; i < out.length; i++) {
    out[i] = parseInt(clean.slice(i * 2, i * 2 + 2), 16);
  }
  return out;
}

function base64ToBytes(b64: string): Uint8Array {
  const normalized = b64.trim().replace(/-/g, '+').replace(/_/g, '/');
  const pad = normalized.length % 4 === 0 ? '' : '='.repeat(4 - (normalized.length % 4));
  const binary = atob(normalized + pad);
  const bytes = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i++) {
    bytes[i] = binary.charCodeAt(i);
  }
  return bytes;
}

function isHexString(s: string): boolean {
  const t = s.trim().replace(/^0x/i, '');
  return t.length >= 2 && t.length % 2 === 0 && /^[0-9a-fA-F]+$/.test(t);
}

function cloneBytes(u: Uint8Array): Uint8Array {
  const c = new Uint8Array(u.byteLength);
  c.set(u);
  return c;
}

function toBufferSource(u: Uint8Array): BufferSource {
  const copy = new Uint8Array(u.byteLength);
  copy.set(u);
  return copy;
}

/** CryptoJS WordArray from raw bytes (big-endian words). */
function u8ToWordArray(u8: Uint8Array): CryptoJS.lib.WordArray {
  const words: number[] = [];
  for (let i = 0; i < u8.length; i += 4) {
    words.push(
      ((u8[i] ?? 0) << 24) |
        ((u8[i + 1] ?? 0) << 16) |
        ((u8[i + 2] ?? 0) << 8) |
        (u8[i + 3] ?? 0)
    );
  }
  return CryptoJS.lib.WordArray.create(words, u8.length);
}

function buildCryptoJsKeys(): CryptoJS.lib.WordArray[] {
  const hex = CryptoJS.enc.Hex.parse(ENCRYPTION_KEY_HEX);
  const utf8 = CryptoJS.enc.Utf8.parse(ENCRYPTION_KEY_HEX);
  const candidates = [
    hex,
    utf8,
    CryptoJS.SHA256(hex),
    CryptoJS.SHA256(utf8),
    hex.clone().concat(hex)
  ];
  const seen = new Set<string>();
  const out: CryptoJS.lib.WordArray[] = [];
  for (const k of candidates) {
    const b = k.sigBytes;
    if (b !== 16 && b !== 24 && b !== 32) continue;
    const id = k.toString(CryptoJS.enc.Hex);
    if (seen.has(id)) continue;
    seen.add(id);
    out.push(k);
  }
  return out;
}

function aesCbcDecryptCryptoJS(
  key: CryptoJS.lib.WordArray,
  iv: CryptoJS.lib.WordArray,
  ciphertext: CryptoJS.lib.WordArray
): string | null {
  try {
    const dec = CryptoJS.AES.decrypt({ ciphertext } as CryptoJS.lib.CipherParams, key, {
      iv,
      mode: CryptoJS.mode.CBC,
      padding: CryptoJS.pad.Pkcs7
    });
    const s = dec.toString(CryptoJS.enc.Utf8);
    return s && s.length > 0 ? s : null;
  } catch {
    return null;
  }
}

/**
 * OpenSSL / CryptoJS default: base64 payload starting with `Salted__` / `U2FsdGVkX1`,
 * passphrase-derived key+IV inside the blob.
 */
function tryCryptoJsPassphrase(encryptedBase64OrOpenSslString: string): string | null {
  const trimmed = encryptedBase64OrOpenSslString.trim();
  const passCandidates: CryptoJS.lib.WordArray[] = [
    CryptoJS.enc.Utf8.parse(ENCRYPTION_KEY_HEX),
    CryptoJS.enc.Hex.parse(ENCRYPTION_KEY_HEX)
  ];
  for (const pass of passCandidates) {
    try {
      const dec = CryptoJS.AES.decrypt(trimmed, pass);
      const s = dec.toString(CryptoJS.enc.Utf8);
      if (s && s.length > 0) return s;
    } catch {
      /* continue */
    }
  }
  try {
    const dec = CryptoJS.AES.decrypt(trimmed, ENCRYPTION_KEY_HEX);
    const s = dec.toString(CryptoJS.enc.Utf8);
    if (s && s.length > 0) return s;
  } catch {
    /* continue */
  }
  return null;
}

function tryLayoutsCryptoJs(
  raw: Uint8Array,
  keys: CryptoJS.lib.WordArray[],
  errors: string[]
): string | null {
  const layouts: Array<{ name: string; iv: Uint8Array; ct: Uint8Array }> = [];

  if (raw.length > 16) {
    layouts.push({ name: 'iv_prefix', iv: raw.slice(0, 16), ct: raw.slice(16) });
    layouts.push({ name: 'iv_suffix', iv: raw.slice(-16), ct: raw.slice(0, -16) });
  }
  if (raw.length >= 16 && raw.length % 16 === 0) {
    layouts.push({ name: 'zero_iv', iv: new Uint8Array(16), ct: raw });
  }

  for (const b of layouts) {
    if (b.ct.length === 0 || b.ct.length % 16 !== 0) continue;
    const ivWa = u8ToWordArray(b.iv);
    const ctWa = u8ToWordArray(b.ct);
    for (const key of keys) {
      const plain = aesCbcDecryptCryptoJS(key, ivWa, ctWa);
      if (plain) return plain;
    }
    errors.push(`${b.name}: CryptoJS CBC failed for all keys`);
  }
  return null;
}

async function buildWebCryptoKeys(hexDecodedKey: Uint8Array): Promise<Uint8Array[]> {
  const utf8Key = new TextEncoder().encode(ENCRYPTION_KEY_HEX);
  const shaOfHexBytes = new Uint8Array(
    await crypto.subtle.digest('SHA-256', toBufferSource(cloneBytes(hexDecodedKey)))
  );
  const shaOfUtf8Secret = new Uint8Array(
    await crypto.subtle.digest('SHA-256', toBufferSource(cloneBytes(utf8Key)))
  );
  const doubled = new Uint8Array(32);
  doubled.set(hexDecodedKey, 0);
  doubled.set(hexDecodedKey, 16);

  const raw = [hexDecodedKey, utf8Key, shaOfHexBytes, shaOfUtf8Secret, doubled];
  const seen = new Set<string>();
  const out: Uint8Array[] = [];
  for (const k of raw) {
    if (k.length !== 16 && k.length !== 24 && k.length !== 32) continue;
    const sig = Array.from(k).join(',');
    if (seen.has(sig)) continue;
    seen.add(sig);
    out.push(k);
  }
  return out;
}

async function aesCbcDecryptWeb(
  keyMaterial: Uint8Array,
  iv: Uint8Array,
  ciphertext: Uint8Array
): Promise<string> {
  const len = keyMaterial.length * 8;
  if (len !== 128 && len !== 192 && len !== 256) {
    throw new Error('Invalid AES key length');
  }
  const key = await crypto.subtle.importKey(
    'raw',
    toBufferSource(cloneBytes(keyMaterial)),
    { name: 'AES-CBC', length: len },
    false,
    ['decrypt']
  );
  const decrypted = await crypto.subtle.decrypt(
    { name: 'AES-CBC', iv: toBufferSource(cloneBytes(iv)) },
    key,
    toBufferSource(cloneBytes(ciphertext))
  );
  return new TextDecoder('utf-8', { fatal: false }).decode(decrypted);
}

/** AES-GCM: 12-byte nonce prefix, ciphertext+authTag (tag 128-bit = 16 bytes at end). */
async function tryAesGcmWeb(nonce: Uint8Array, ctAndTag: Uint8Array): Promise<string | null> {
  if (nonce.length !== 12 || ctAndTag.length < 16) return null;
  const hexKey = hexToBytes(ENCRYPTION_KEY_HEX);
  const keys = await buildWebCryptoKeys(hexKey);
  for (const keyMat of keys) {
    try {
      const len = keyMat.length * 8;
      if (len !== 128 && len !== 192 && len !== 256) continue;
      const key = await crypto.subtle.importKey(
        'raw',
        toBufferSource(cloneBytes(keyMat)),
        { name: 'AES-GCM', length: len },
        false,
        ['decrypt']
      );
      const dec = await crypto.subtle.decrypt(
        { name: 'AES-GCM', iv: toBufferSource(cloneBytes(nonce)), tagLength: 128 },
        key,
        toBufferSource(cloneBytes(ctAndTag))
      );
      const s = new TextDecoder('utf-8', { fatal: false }).decode(dec);
      if (s) return s;
    } catch {
      /* next key */
    }
  }
  return null;
}

function tryDecodeToBytes(s: string): Uint8Array {
  const t = s.trim();
  if (t.startsWith('{')) {
    throw new Error('nested JSON not supported');
  }
  try {
    return base64ToBytes(t);
  } catch {
    if (isHexString(t)) {
      return hexToBytes(t.replace(/^0x/i, ''));
    }
  }
  throw new Error('iv/ciphertext field is not base64 or hex');
}

@Injectable({
  providedIn: 'root'
})
export class DecryptService {
  private readonly keyBytes = hexToBytes(ENCRYPTION_KEY_HEX);
  private webKeysPromise: Promise<Uint8Array[]> | null = null;
  private readonly cryptoJsKeys = buildCryptoJsKeys();

  private getWebKeys(): Promise<Uint8Array[]> {
    if (!this.webKeysPromise) {
      this.webKeysPromise = buildWebCryptoKeys(this.keyBytes);
    }
    return this.webKeysPromise;
  }

  decrypt(encryptedData: string): Observable<any> {
    return from(
      this.decryptToPlaintext(encryptedData).then((text) => this.parseDecryptedPayload(text))
    );
  }

  private parseDecryptedPayload(text: string): unknown {
    let t = text.replace(/\0+$/g, '').trim();
    if (
      (t.startsWith('{') && t.endsWith('}')) ||
      (t.startsWith('[') && t.endsWith(']')) ||
      t.startsWith('"')
    ) {
      try {
        return JSON.parse(t);
      } catch {
        /* keep string */
      }
    }
    return t;
  }

  private async decryptToPlaintext(encryptedData: string): Promise<string> {
    const s = encryptedData.trim();
    const errors: string[] = [];

    // 1) CryptoJS / OpenSSL salted string (same as many PHP `encrypt()` helpers)
    const passPlain = tryCryptoJsPassphrase(s);
    if (passPlain) return passPlain;

    // 2) JSON envelope { iv, ciphertext | value | data | … }
    if (s.startsWith('{')) {
      try {
        const o = JSON.parse(s) as Record<string, unknown>;
        const ivRaw = o['iv'] ?? o['IV'] ?? o['nonce'];
        const ctRaw =
          o['value'] ??
          o['ciphertext'] ??
          o['encrypted'] ??
          o['payload'] ??
          o['data'];
        if (typeof ivRaw === 'string' && typeof ctRaw === 'string') {
          try {
            const iv = tryDecodeToBytes(String(ivRaw));
            const ct = tryDecodeToBytes(String(ctRaw));
            if (iv.length === 16 && ct.length > 0 && ct.length % 16 === 0) {
              const ivWa = u8ToWordArray(iv);
              const ctWa = u8ToWordArray(ct);
              for (const key of this.cryptoJsKeys) {
                const p = aesCbcDecryptCryptoJS(key, ivWa, ctWa);
                if (p) return p;
              }
              errors.push('json iv/ct: CryptoJS CBC failed');
            }
            if (iv.length === 12 && ct.length >= 16) {
              const gcm = await tryAesGcmWeb(iv, ct);
              if (gcm) return gcm;
              errors.push('json iv/ct: AES-GCM failed');
            }
          } catch (e) {
            errors.push(`json iv/ct: ${(e as Error).message}`);
          }
        }
      } catch {
        /* continue */
      }
    }

    let raw: Uint8Array;
    try {
      raw = base64ToBytes(s);
    } catch {
      if (isHexString(s)) {
        raw = hexToBytes(s.replace(/^0x/i, ''));
      } else {
        throw new Error('Payload is not valid base64 or hex');
      }
    }

    if (raw.length < 16) {
      throw new Error('Encrypted payload too short');
    }

    // 3) CryptoJS CBC layouts (most compatible with mixed backends)
    const cj = tryLayoutsCryptoJs(raw, this.cryptoJsKeys, errors);
    if (cj) return cj;

    // 4) AES-GCM: 12-byte nonce + (cipher || tag)
    if (raw.length >= 28) {
      const gcm = await tryAesGcmWeb(raw.slice(0, 12), raw.slice(12));
      if (gcm) return gcm;
    }

    // 5) Web Crypto AES-CBC fallback
    const webKeys = await this.getWebKeys();
    const blocks: Array<{ name: string; iv: Uint8Array; ct: Uint8Array }> = [];
    if (raw.length > 16) {
      blocks.push({ name: 'iv_prefix', iv: raw.slice(0, 16), ct: raw.slice(16) });
      blocks.push({ name: 'iv_suffix', iv: raw.slice(-16), ct: raw.slice(0, -16) });
    }
    if (raw.length >= 16 && raw.length % 16 === 0) {
      blocks.push({ name: 'zero_iv', iv: new Uint8Array(16), ct: raw });
    }
    for (const b of blocks) {
      if (b.ct.length === 0 || b.ct.length % 16 !== 0) continue;
      for (const keyMat of webKeys) {
        try {
          return await aesCbcDecryptWeb(keyMat, b.iv, b.ct);
        } catch {
          /* try next */
        }
      }
      errors.push(`${b.name}: Web Crypto CBC failed`);
    }

    throw new Error(`AES decrypt failed (${errors.slice(0, 8).join('; ') || 'all strategies exhausted'})`);
  }

  isEncrypted(data: unknown): boolean {
    if (typeof data !== 'string') {
      return false;
    }
    const t = data.trim();
    if (t.length >= 20 && /^[A-Za-z0-9+/=_-]+$/.test(t)) {
      return true;
    }
    if (t.startsWith('{')) {
      try {
        const o = JSON.parse(t) as Record<string, unknown>;
        const ivRaw = o['iv'] ?? o['IV'] ?? o['nonce'];
        const ctRaw =
          o['value'] ?? o['data'] ?? o['ciphertext'] ?? o['encrypted'] ?? o['payload'];
        return typeof ivRaw === 'string' && typeof ctRaw === 'string';
      } catch {
        return false;
      }
    }
    return false;
  }

  isEncryptedResponse(response: unknown): boolean {
    if (typeof response === 'string') {
      return this.isEncrypted(response);
    }
    if (response && typeof response === 'object') {
      const r = response as Record<string, unknown>;
      if (typeof r['encrypted_data'] === 'string') {
        return this.isEncrypted(r['encrypted_data']);
      }
      if (typeof r['data'] === 'string') {
        return this.isEncrypted(r['data']);
      }
      if (isIvWrappedCipherObject(r)) {
        return true;
      }
      const nested = r['data'];
      if (nested && typeof nested === 'object') {
        const n = nested as Record<string, unknown>;
        if (typeof n['encrypted_data'] === 'string') {
          return this.isEncrypted(n['encrypted_data']);
        }
        if (typeof n['data'] === 'string') {
          return this.isEncrypted(n['data']);
        }
      }
    }
    return false;
  }

  static extractEncryptedString(body: unknown): string | null {
    if (body == null) return null;
    if (typeof body === 'string') return body;
    if (typeof body !== 'object') return null;
    const r = body as Record<string, unknown>;
    if (isIvWrappedCipherObject(r)) {
      return JSON.stringify(body);
    }
    if (typeof r['encrypted_data'] === 'string') return r['encrypted_data'];
    if (typeof r['data'] === 'string') return r['data'];
    const nested = r['data'];
    if (nested && typeof nested === 'object') {
      const n = nested as Record<string, unknown>;
      if (typeof n['encrypted_data'] === 'string') return n['encrypted_data'];
      if (typeof n['data'] === 'string') return n['data'];
    }
    return null;
  }
}

function isIvWrappedCipherObject(r: Record<string, unknown>): boolean {
  const ivRaw = r['iv'] ?? r['IV'] ?? r['nonce'];
  if (typeof ivRaw !== 'string') return false;
  const ct =
    (typeof r['value'] === 'string' ? r['value'] : null) ??
    (typeof r['ciphertext'] === 'string' ? r['ciphertext'] : null) ??
    (typeof r['encrypted'] === 'string' ? r['encrypted'] : null) ??
    (typeof r['payload'] === 'string' ? r['payload'] : null) ??
    (typeof r['data'] === 'string' ? r['data'] : null);
  return typeof ct === 'string';
}
